import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2, Users, UserCircle, Search, Loader2, Mail, Phone, MapPin, 
  PoundSterling, Calendar, CheckCircle, Clock, Send, Home, RefreshCw,
  Settings, Percent, AlertCircle, Shield, FileCheck,
  ClipboardList, FileText, Plus, Wrench, ExternalLink, ChevronRight, ChevronLeft,
  LayoutDashboard, User, CalendarDays, FileInput, Eye, BedDouble, Bath,
  Edit, Save, Trash2, Filter, FileBarChart
} from "lucide-react";
import { API } from "@/App";
import { PostcodeLookup } from "@/components/PostcodeLookup";

// Sidebar menu items - clean and focused
const MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'properties', label: 'Properties', icon: Building2 },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench },
  { id: 'compliance', label: 'Compliance', icon: Shield },
  { id: 'inspections', label: 'Inspections', icon: ClipboardList },
  { id: 'landlords', label: 'Landlords', icon: User },
  { id: 'applicants', label: 'Applicants', icon: Users },
  { id: 'instructions', label: 'Instructions', icon: FileInput },
  { id: 'diary', label: 'Diary', icon: CalendarDays },
];

// Secondary menu (collapsed by default)
const SECONDARY_MENU = [
  { id: 'tenants', label: 'Tenants', icon: Users },
  { id: 'contractors', label: 'Contractors', icon: Wrench },
  { id: 'reports', label: 'Reports', icon: FileBarChart, isLink: true, href: '/admin/reports' },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export const LettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [maintenanceJobs, setMaintenanceJobs] = useState([]);
  const [activeSection, setActiveSection] = useState(searchParams.get("section") || "dashboard");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [managementTypeFilter, setManagementTypeFilter] = useState(searchParams.get("management_type") || "all");
  const [tenancyStatusFilter, setTenancyStatusFilter] = useState(searchParams.get("status") || "all");
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [applicantsLoading, setApplicantsLoading] = useState(false);
  const [applicantSearch, setApplicantSearch] = useState(searchParams.get("applicant_q") || "");
  const [propertiesPage, setPropertiesPage] = useState(() => parseInt(searchParams.get("page") || "1", 10) || 1);
  const [propertiesPerPage, setPropertiesPerPage] = useState(50);
  
  // Modal states
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [showAddLandlordModal, setShowAddLandlordModal] = useState(false);
  const [showAddApplicantModal, setShowAddApplicantModal] = useState(false);
  const [showAddContractorModal, setShowAddContractorModal] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [landlordForm, setLandlordForm] = useState({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    town: '',
    postcode: '',
    bank_name: '',
    account_number: '',
    sort_code: ''
  });
  const [savingLandlord, setSavingLandlord] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [savingPropertyEdit, setSavingPropertyEdit] = useState(false);
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(null);
  const [deletingProperty, setDeletingProperty] = useState(false);
  // Tenant edit state
  const [editingTenantProp, setEditingTenantProp] = useState(null);
  const [editTenantName, setEditTenantName] = useState("");
  const [savingTenantEdit, setSavingTenantEdit] = useState(false);
  // Multi-select state for bulk delete
  const [selectedProperties, setSelectedProperties] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [contractorForm, setContractorForm] = useState({
    name: '',
    email: '',
    phone: '',
    specialty: '',
    company_name: '',
    notes: ''
  });
  const [savingContractor, setSavingContractor] = useState(false);
  const [invitingContractor, setInvitingContractor] = useState(null);
  const [newApplicant, setNewApplicant] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    current_address: '',
    current_postcode: '',
    applicant_type: 'tenant',
    max_rent: '',
    min_bedrooms: '',
    preferred_areas: '',
    move_in_date: '',
    employment_status: '',
    has_pets: false,
    is_smoker: false,
    notes: '',
    // Letting classification
    letting_long_let: false,
    letting_short_let: false,
    letting_student: false,
    // Furnishing preferences
    furnishing_fully: false,
    furnishing_part: false,
    furnishing_unfurnished: false,
    furnishing_unfurnished_white: false,
    furnishing_either: false,
    // Additional contact
    use_dear_sir_madam: false,
    additional_contact_name: '',
    additional_contact_phone: '',
    additional_contact_email: ''
  });
  const [savingApplicant, setSavingApplicant] = useState(false);
  const [showAdditionalContact, setShowAdditionalContact] = useState(false);
  
  // Property form state
  const [propertyForm, setPropertyForm] = useState({
    address_line1: "",
    address_line2: "",
    town: "",
    postcode: "",
    landlord_id: "",
    rent_amount: "",
    property_type: "house",
    bedrooms: "",
    bathrooms: "",
    createNewLandlord: false,
    newLandlord: null
  });
  const [savingProperty, setSavingProperty] = useState(false);
  
  // Keep URL in sync with the current section / search / filter / page so
  // returning here from a property detail restores the exact view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (activeSection && activeSection !== 'dashboard') next.set('section', activeSection); else next.delete('section');
    if (searchTerm) next.set('q', searchTerm); else next.delete('q');
    if (managementTypeFilter && managementTypeFilter !== 'all') next.set('management_type', managementTypeFilter); else next.delete('management_type');
    if (tenancyStatusFilter && tenancyStatusFilter !== 'all') next.set('status', tenancyStatusFilter); else next.delete('status');
    if (propertiesPage > 1) next.set('page', String(propertiesPage)); else next.delete('page');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, searchTerm, managementTypeFilter, tenancyStatusFilter, propertiesPage]);

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch applicants when section changes to applicants
  useEffect(() => {
    if (activeSection === 'applicants' && applicants.length === 0) {
      fetchApplicants();
    }
  }, [activeSection]);

  const fetchApplicants = async () => {
    setApplicantsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/lettings/applicants`, { headers });
      setApplicants(res.data?.applicants || []);
    } catch (error) {
      console.error("Error fetching applicants:", error);
    } finally {
      setApplicantsLoading(false);
    }
  };

  const handleAddApplicant = async () => {
    if (!newApplicant.first_name || !newApplicant.last_name) {
      alert("First name and last name are required");
      return;
    }
    
    setSavingApplicant(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...newApplicant,
        max_rent: newApplicant.max_rent ? parseFloat(newApplicant.max_rent) : null,
        min_bedrooms: newApplicant.min_bedrooms ? parseInt(newApplicant.min_bedrooms) : null
      };
      await axios.post(`${API}/lettings/applicants`, payload, { headers });
      
      // Reset form and close modal
      setNewApplicant({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        mobile: '',
        current_address: '',
        current_postcode: '',
        applicant_type: 'tenant',
        max_rent: '',
        min_bedrooms: '',
        preferred_areas: '',
        move_in_date: '',
        employment_status: '',
        has_pets: false,
        is_smoker: false,
        notes: '',
        letting_long_let: false,
        letting_short_let: false,
        letting_student: false,
        furnishing_fully: false,
        furnishing_part: false,
        furnishing_unfurnished: false,
        furnishing_unfurnished_white: false,
        furnishing_either: false,
        use_dear_sir_madam: false,
        additional_contact_name: '',
        additional_contact_phone: '',
        additional_contact_email: ''
      });
      setShowAddApplicantModal(false);
      setShowAdditionalContact(false);
      
      // Refresh applicants list
      fetchApplicants();
    } catch (error) {
      console.error("Error adding applicant:", error);
      alert("Failed to add applicant");
    } finally {
      setSavingApplicant(false);
    }
  };

  // Handle add property
  const handleAddProperty = async () => {
    if (!propertyForm.address_line1) {
      toast.error("Address line 1 is required");
      return;
    }
    
    // If creating new landlord, validate required fields
    if (propertyForm.createNewLandlord) {
      if (!propertyForm.newLandlord?.name) {
        toast.error("Landlord name is required");
        return;
      }
      if (!propertyForm.newLandlord?.email) {
        toast.error("Landlord email is required");
        return;
      }
    }
    
    setSavingProperty(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      let landlordId = propertyForm.landlord_id;
      
      // Create new landlord first if needed
      if (propertyForm.createNewLandlord && propertyForm.newLandlord?.name) {
        const landlordRes = await axios.post(`${API}/lettings/landlords`, propertyForm.newLandlord, { headers });
        landlordId = landlordRes.data.landlord_id;
        toast.success("Landlord created");
      }
      
      const response = await axios.post(`${API}/lettings/properties`, {
        address_line1: propertyForm.address_line1,
        address_line2: propertyForm.address_line2 || null,
        town: propertyForm.town || null,
        postcode: propertyForm.postcode || null,
        landlord_id: landlordId || null,
        rent_amount: propertyForm.rent_amount ? parseFloat(propertyForm.rent_amount) : null,
        property_type: propertyForm.property_type || "house",
        bedrooms: propertyForm.bedrooms ? parseInt(propertyForm.bedrooms) : null,
        bathrooms: propertyForm.bathrooms ? parseInt(propertyForm.bathrooms) : null
      }, { headers });
      
      toast.success("Property added successfully");
      setShowAddPropertyModal(false);
      setPropertyForm({
        address_line1: "", address_line2: "", town: "", postcode: "",
        landlord_id: "", rent_amount: "", property_type: "house", bedrooms: "", bathrooms: "",
        createNewLandlord: false, newLandlord: null
      });
      
      // Refresh data
      fetchData();
      
      // Navigate to the new property
      if (response.data?.property_id) {
        navigate(`/lettings/property/${response.data.property_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add property");
    } finally {
      setSavingProperty(false);
    }
  };

  // Handle add landlord
  const handleAddLandlord = async () => {
    if (!landlordForm.name) {
      toast.error("Name is required");
      return;
    }
    
    setSavingLandlord(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/lettings/landlords`, landlordForm, { headers });
      
      toast.success("Landlord added successfully");
      setShowAddLandlordModal(false);
      setLandlordForm({
        name: '', email: '', phone: '', address_line1: '', address_line2: '',
        town: '', postcode: '', bank_name: '', account_number: '', sort_code: ''
      });
      
      // Refresh landlords list
      fetchData();
      
      // Navigate to the new landlord detail page if it exists
      if (response.data?.landlord_id) {
        navigate(`/admin/landlord/${response.data.landlord_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add landlord");
    } finally {
      setSavingLandlord(false);
    }
  };

  // Handle edit property (update landlord)
  const handleEditProperty = (prop, e) => {
    e?.stopPropagation();
    setEditingProperty({
      property_id: prop.property_id,
      address: prop.address || prop.address_line1,
      landlord_id: prop.landlord_id || ""
    });
    setShowEditPropertyModal(true);
  };

  const handleSavePropertyEdit = async () => {
    if (!editingProperty) return;
    
    setSavingPropertyEdit(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/properties/${editingProperty.property_id}`, {
        landlord_id: editingProperty.landlord_id || null
      }, { headers });
      
      toast.success("Property updated");
      setShowEditPropertyModal(false);
      setEditingProperty(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update property");
    } finally {
      setSavingPropertyEdit(false);
    }
  };

  const handleDeleteProperty = async () => {
    if (!showDeletePropertyModal) return;
    
    setDeletingProperty(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/lettings/properties/${showDeletePropertyModal.property_id}`, { headers });
      
      toast.success("Property deleted successfully");
      setShowDeletePropertyModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete property");
    } finally {
      setDeletingProperty(false);
    }
  };

  // Update tenant name on property
  const handleUpdateTenantName = async (clearTenant = false) => {
    if (!editingTenantProp) return;
    setSavingTenantEdit(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lettings/properties/${editingTenantProp.property_id}`, {
        tenant_name: clearTenant ? "" : editTenantName,
        tenant_email: clearTenant ? "" : editingTenantProp.tenant_email
      }, { headers });
      toast.success(clearTenant ? "Tenant cleared from property" : "Tenant name updated");
      setEditingTenantProp(null);
      setEditTenantName("");
      fetchData();
    } catch (error) {
      toast.error("Failed to update tenant");
    } finally {
      setSavingTenantEdit(false);
    }
  };

  // Bulk delete selected properties
  const handleBulkDeleteProperties = async () => {
    if (selectedProperties.size === 0) return;
    
    setBulkDeleting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const deletePromises = Array.from(selectedProperties).map(propertyId =>
        axios.delete(`${API}/lettings/properties/${propertyId}`, { headers })
      );
      
      await Promise.all(deletePromises);
      
      toast.success(`${selectedProperties.size} properties deleted successfully`);
      setSelectedProperties(new Set());
      setShowBulkDeleteModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete some properties");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Toggle property selection
  const togglePropertySelection = (propertyId, e) => {
    e.stopPropagation();
    setSelectedProperties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  // Select/deselect all visible properties
  const toggleSelectAll = () => {
    const visibleProps = filteredProperties.slice(
      (propertiesPage - 1) * propertiesPerPage, 
      propertiesPage * propertiesPerPage
    );
    
    const allSelected = visibleProps.every(p => selectedProperties.has(p.property_id));
    
    if (allSelected) {
      // Deselect all visible
      setSelectedProperties(prev => {
        const newSet = new Set(prev);
        visibleProps.forEach(p => newSet.delete(p.property_id));
        return newSet;
      });
    } else {
      // Select all visible
      setSelectedProperties(prev => {
        const newSet = new Set(prev);
        visibleProps.forEach(p => newSet.add(p.property_id));
        return newSet;
      });
    }
  };

  const fetchData = async (mgmtType = managementTypeFilter) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build properties URL with filter
      const propsUrl = mgmtType && mgmtType !== "all" 
        ? `${API}/lettings/properties?management_type=${mgmtType}`
        : `${API}/lettings/properties`;
      
      const [statsRes, propsRes, landlordsRes, tenantsRes, complianceRes, jobsRes] = await Promise.all([
        axios.get(`${API}/lettings/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(propsUrl, { headers }).catch(() => ({ data: { properties: [] } })),
        axios.get(`${API}/lettings/landlords`, { headers }).catch(() => ({ data: { landlords: [] } })),
        axios.get(`${API}/lettings/tenants`, { headers }).catch(() => ({ data: { tenants: [] } })),
        axios.get(`${API}/compliance/alerts`, { headers }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/jobs`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setProperties(propsRes.data?.properties || []);
      setLandlords(landlordsRes.data?.landlords || []);
      setTenants(tenantsRes.data?.tenants || []);
      setComplianceAlerts(complianceRes.data?.alerts || []);
      setMaintenanceJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle management type filter change
  const handleManagementTypeChange = (value) => {
    setManagementTypeFilter(value);
    setPropertiesPage(1); // Reset to page 1 when filter changes
    // Update URL query param
    if (value && value !== "all") {
      setSearchParams({ management_type: value });
    } else {
      setSearchParams({});
    }
    fetchData(value);
  };

  // Fetch contractors when section changes
  const fetchContractors = async () => {
    try {
      const response = await axios.get(`${API}/contractors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractors(response.data?.contractors || response.data || []);
    } catch (error) {
      console.error("Error fetching contractors:", error);
    }
  };

  // Load contractors when section changes to contractors
  useEffect(() => {
    if (activeSection === 'contractors') {
      fetchContractors();
    }
  }, [activeSection]);

  // Add new contractor
  const handleAddContractor = async () => {
    if (!contractorForm.name || !contractorForm.phone) {
      toast.error("Please enter contractor name and phone number");
      return;
    }
    setSavingContractor(true);
    try {
      await axios.post(`${API}/contractors`, contractorForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contractor added successfully");
      setShowAddContractorModal(false);
      setContractorForm({ name: '', email: '', phone: '', specialty: '', company_name: '', notes: '' });
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add contractor");
    } finally {
      setSavingContractor(false);
    }
  };

  // Send portal invite to contractor
  const handleSendInvite = async (contractor) => {
    if (!contractor.email) {
      toast.error("Contractor has no email address");
      return;
    }
    setInvitingContractor(contractor.contractor_id || contractor.id);
    try {
      await axios.post(`${API}/contractor-management/invite`, {
        name: contractor.name,
        email: contractor.email,
        phone: contractor.phone || '',
        company: contractor.company_name || contractor.company || '',
        specialty: contractor.specialty || 'handyman'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite sent to ${contractor.email}!`);
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setInvitingContractor(null);
    }
  };

  // Get status display info for contractor
  const getContractorStatus = (contractor) => {
    const status = contractor.status;
    if (status === 'active') {
      return { label: 'Active', bgColor: 'bg-green-100', textColor: 'text-green-700', icon: CheckCircle, isActive: true };
    } else if (status === 'pending_approval') {
      return { label: 'Pending Approval', bgColor: 'bg-amber-100', textColor: 'text-amber-700', icon: Clock, isActive: false };
    } else if (status === 'invited' || status === 'onboarding') {
      return { label: 'Invited', bgColor: 'bg-blue-100', textColor: 'text-blue-700', icon: Send, isActive: false };
    } else if (status === 'rejected') {
      return { label: 'Rejected', bgColor: 'bg-red-100', textColor: 'text-red-700', icon: AlertCircle, isActive: false };
    } else if (status === 'suspended') {
      return { label: 'Suspended', bgColor: 'bg-slate-100', textColor: 'text-slate-700', icon: AlertCircle, isActive: false };
    }
    return { label: 'Not Invited', bgColor: 'bg-slate-100', textColor: 'text-slate-500', icon: null, isActive: false };
  };

  // Delete contractor
  const handleDeleteContractor = async (contractorId) => {
    if (!window.confirm('Are you sure you want to delete this contractor?')) {
      return;
    }
    try {
      await axios.delete(`${API}/contractors/${contractorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contractor deleted');
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contractor');
    }
  };

  // Filter helpers
  const activeJobs = maintenanceJobs.filter(j => !['completed', 'resolved'].includes(j.status));
  const urgentCompliance = complianceAlerts.filter(a => a.status === 'expired' || a.days_until_expiry <= 7);
  const occupiedProperties = properties.filter(p => p.tenant_name || p.current_tenant_id);
  
  // Search and status filter for properties
  const filteredProperties = properties.filter(p => {
    // Tenancy status filter
    if (tenancyStatusFilter === "active") {
      if (!p.tenant_name && !p.current_tenant_id && !p.tenant_id) return false;
    } else if (tenancyStatusFilter === "vacant") {
      if (p.tenant_name || p.current_tenant_id || p.tenant_id) return false;
    } else if (tenancyStatusFilter === "managed") {
      if (!p.management_type || !p.management_type.toLowerCase().includes("full")) return false;
    }
    
    // Search filter
    if (!searchTerm) return true;
    return (
      p.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.postcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.landlord_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar Navigation */}
      <aside className="w-56 bg-white border-r border-slate-200 flex-shrink-0 sticky top-0 h-screen">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold text-slate-800">Lettings</h1>
          <p className="text-xs text-muted-foreground">Goodacres Residential</p>
        </div>
        
        <nav className="p-3 space-y-1">
          {MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            const hasAlert = item.id === 'compliance' && urgentCompliance.length > 0;
            const hasJobs = item.id === 'maintenance' && activeJobs.length > 0;
            
            // Handle click - navigate to compliance page if clicking compliance
            const handleClick = () => {
              if (item.id === 'compliance') {
                navigate('/admin/compliance');
              } else if (item.id === 'inspections') {
                navigate('/inventory');
              } else {
                setActiveSection(item.id);
              }
            };
            
            return (
              <button
                key={item.id}
                onClick={handleClick}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-all ${
                  isActive 
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <Icon className="w-4 h-4" />
                  {item.label}
                </span>
                {hasAlert && (
                  <Badge className="bg-red-500 text-white text-xs px-1.5">{urgentCompliance.length}</Badge>
                )}
                {hasJobs && (
                  <Badge className="bg-amber-500 text-white text-xs px-1.5">{activeJobs.length}</Badge>
                )}
              </button>
            );
          })}
          
          {/* Divider */}
          <div className="border-t my-3"></div>
          
          {/* More button to show secondary menu */}
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg"
          >
            <span>More...</span>
            <ChevronRight className={`w-4 h-4 transition-transform ${showMoreMenu ? 'rotate-90' : ''}`} />
          </button>
          
          {showMoreMenu && (
            <div className="pl-2 space-y-1">
              {SECONDARY_MENU.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                
                // Handle external links (like Reports)
                if (item.isLink && item.href) {
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.href)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all text-slate-500 hover:bg-slate-100"
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </button>
                  );
                }
                
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all ${
                      isActive 
                        ? 'bg-blue-50 text-blue-700 font-medium' 
                        : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </nav>
        
        {/* Quick Stats at bottom of sidebar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-slate-50">
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="bg-white rounded-lg p-2 border">
              <p className="text-lg font-bold text-slate-700">{properties.length}</p>
              <p className="text-xs text-muted-foreground">Properties</p>
            </div>
            <div className="bg-white rounded-lg p-2 border">
              <p className="text-lg font-bold text-emerald-600">{occupiedProperties.length}</p>
              <p className="text-xs text-muted-foreground">Occupied</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b px-6 py-4 sticky top-14 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-800 capitalize">{activeSection}</h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input 
                  placeholder="Search..." 
                  className="pl-9 w-64"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPropertiesPage(1); }}
                />
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* DASHBOARD SECTION */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {/* Welcome / Quick Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Total Properties</p>
                        <p className="text-2xl font-bold text-blue-700">{properties.length}</p>
                      </div>
                      <Building2 className="w-8 h-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Monthly Rent</p>
                        <p className="text-2xl font-bold text-emerald-700">
                          £{(stats?.total_monthly_rent || 0).toLocaleString()}
                        </p>
                      </div>
                      <PoundSterling className="w-8 h-8 text-emerald-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-600 font-medium">Active Jobs</p>
                        <p className="text-2xl font-bold text-amber-700">{activeJobs.length}</p>
                      </div>
                      <Wrench className="w-8 h-8 text-amber-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className={`bg-gradient-to-br ${urgentCompliance.length > 0 ? 'from-red-50 border-red-200' : 'from-slate-50 border-slate-200'} to-white`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs font-medium ${urgentCompliance.length > 0 ? 'text-red-600' : 'text-slate-600'}`}>Compliance Alerts</p>
                        <p className={`text-2xl font-bold ${urgentCompliance.length > 0 ? 'text-red-700' : 'text-slate-700'}`}>{complianceAlerts.length}</p>
                      </div>
                      <Shield className={`w-8 h-8 ${urgentCompliance.length > 0 ? 'text-red-400' : 'text-slate-400'}`} />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-4 gap-4">
                <Card 
                  className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500"
                  onClick={() => setActiveSection('properties')}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">View Properties</p>
                      <p className="text-sm text-muted-foreground">{properties.length} total</p>
                    </div>
                  </CardContent>
                </Card>
                <Card 
                  className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-amber-500"
                  onClick={() => setActiveSection('maintenance')}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">Maintenance</p>
                      <p className="text-sm text-muted-foreground">{activeJobs.length} active jobs</p>
                    </div>
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer hover:shadow-md transition-shadow border-l-4 ${urgentCompliance.length > 0 ? 'border-l-red-500' : 'border-l-emerald-500'}`}
                  onClick={() => navigate('/admin/compliance')}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${urgentCompliance.length > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                      <Shield className={`w-5 h-5 ${urgentCompliance.length > 0 ? 'text-red-600' : 'text-emerald-600'}`} />
                    </div>
                    <div>
                      <p className="font-medium">Compliance</p>
                      <p className={`text-sm ${urgentCompliance.length > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                        {urgentCompliance.length > 0 ? `${urgentCompliance.length} urgent` : 'All clear'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card 
                  className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-purple-500"
                  onClick={() => setActiveSection('landlords')}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Landlords</p>
                      <p className="text-sm text-muted-foreground">{landlords.length} total</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity / Alerts */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Urgent Compliance */}
                {urgentCompliance.length > 0 && (
                  <Card className="border-red-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-5 h-5" />
                        Urgent Compliance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {urgentCompliance.slice(0, 5).map((alert, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{alert.property_address || alert.address}</p>
                              <p className="text-xs text-red-600">{alert.type} - {alert.status}</p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/lettings/property/${alert.property_id}`)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Active Maintenance Jobs */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-amber-600" />
                      Active Maintenance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {activeJobs.length === 0 ? (
                      <div className="text-center py-6 text-muted-foreground">
                        <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No active jobs</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {activeJobs.slice(0, 5).map((job) => (
                          <div key={job.job_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{job.issue_type || 'Maintenance'}</p>
                              <p className="text-xs text-muted-foreground">
                                {job.tenant_name || job.property_address || 'Unknown'} • {job.status?.replace(/_/g, ' ')}
                              </p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">{job.status?.replace(/_/g, ' ')}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* PROPERTIES SECTION */}
          {activeSection === 'properties' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    {filteredProperties.length} properties 
                    {searchTerm && ` matching "${searchTerm}"`}
                    {tenancyStatusFilter === "active" && " (active tenancies)"}
                    {tenancyStatusFilter === "vacant" && " (vacant)"}
                    {tenancyStatusFilter === "managed" && " (fully managed)"}
                  </p>
                  {/* Tenancy Status Filter */}
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <Select value={tenancyStatusFilter} onValueChange={(v) => { setTenancyStatusFilter(v); setPropertiesPage(1); }}>
                      <SelectTrigger className="w-[160px] h-9" data-testid="tenancy-status-filter">
                        <SelectValue placeholder="All Properties" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Properties</SelectItem>
                        <SelectItem value="active">Active Tenancies</SelectItem>
                        <SelectItem value="vacant">Vacant</SelectItem>
                        <SelectItem value="managed">Fully Managed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Management Type Filter */}
                  <div className="flex items-center gap-2">
                    <Select value={managementTypeFilter} onValueChange={handleManagementTypeChange}>
                      <SelectTrigger className="w-[180px] h-9" data-testid="management-type-filter">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="fully_managed">Fully Managed</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                        <SelectItem value="find_tenant_only">Find Tenant Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Items Per Page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">Show:</span>
                    <Select value={String(propertiesPerPage)} onValueChange={(v) => { setPropertiesPerPage(Number(v)); setPropertiesPage(1); }}>
                      <SelectTrigger className="w-[80px] h-9" data-testid="properties-per-page">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                        <SelectItem value="500">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={() => setShowAddPropertyModal(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Property
                </Button>
              </div>

              {/* Bulk delete bar - shows when items selected */}
              {selectedProperties.size > 0 && (
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <span className="text-sm font-medium text-red-700">
                    {selectedProperties.size} {selectedProperties.size === 1 ? 'property' : 'properties'} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedProperties(new Set())}
                    >
                      Clear selection
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => setShowBulkDeleteModal(true)}
                      data-testid="bulk-delete-properties-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
                    </Button>
                  </div>
                </div>
              )}

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="py-3 px-4 w-10">
                            <Checkbox 
                              checked={
                                filteredProperties
                                  .slice((propertiesPage - 1) * propertiesPerPage, propertiesPage * propertiesPerPage)
                                  .length > 0 &&
                                filteredProperties
                                  .slice((propertiesPage - 1) * propertiesPerPage, propertiesPage * propertiesPerPage)
                                  .every(p => selectedProperties.has(p.property_id))
                              }
                              onCheckedChange={toggleSelectAll}
                              data-testid="select-all-properties"
                            />
                          </th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Property</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Type</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Landlord</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Tenant</th>
                          <th className="text-right py-3 px-4 font-medium text-sm">Rent</th>
                          <th className="text-center py-3 px-4 font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProperties
                          .slice((propertiesPage - 1) * propertiesPerPage, propertiesPage * propertiesPerPage)
                          .map((prop) => (
                          <tr 
                            key={prop.property_id} 
                            className={`border-b hover:bg-slate-50 cursor-pointer ${selectedProperties.has(prop.property_id) ? 'bg-blue-50' : ''}`}
                            onClick={() => navigate(`/admin/lettings/property/${prop.property_id}`)}
                          >
                            <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                              <Checkbox 
                                checked={selectedProperties.has(prop.property_id)}
                                onCheckedChange={(e) => togglePropertySelection(prop.property_id, { stopPropagation: () => {} })}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePropertySelection(prop.property_id, e);
                                }}
                                data-testid={`select-property-${prop.property_id}`}
                              />
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-medium text-blue-600 hover:underline">{prop.address}</p>
                              <p className="text-xs text-muted-foreground">{prop.postcode}</p>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={
                                prop.management_type === "fully_managed" ? "bg-emerald-100 text-emerald-700" :
                                prop.management_type === "hybrid" ? "bg-blue-100 text-blue-700" :
                                prop.management_type === "find_tenant_only" ? "bg-amber-100 text-amber-700" :
                                "bg-slate-100 text-slate-600"
                              }>
                                {prop.management_type === "fully_managed" ? "Fully Managed" :
                                 prop.management_type === "hybrid" ? "Hybrid" :
                                 prop.management_type === "find_tenant_only" ? "Find Tenant" :
                                 "Not Set"}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              {prop.landlord_name ? (
                                <div>
                                  <p className="text-sm font-medium">{prop.landlord_name}</p>
                                  {prop.landlord_email && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Mail className="w-3 h-3" />{prop.landlord_email}
                                    </p>
                                  )}
                                  {prop.landlord_phone && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                      <Phone className="w-3 h-3" />{prop.landlord_phone}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-amber-600">No landlord</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div 
                                className="flex items-center gap-1 group cursor-pointer hover:bg-slate-100 rounded px-1 -mx-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingTenantProp(prop);
                                  setEditTenantName(prop.tenant_name || "");
                                }}
                                data-testid={`edit-tenant-${prop.property_id}`}
                              >
                                {prop.tenant_name ? (
                                  <div>
                                    <p className="text-sm">{prop.tenant_name}</p>
                                    {prop.tenant_email && (
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Mail className="w-3 h-3" />{prop.tenant_email}
                                      </p>
                                    )}
                                    {prop.tenant_phone && (
                                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Phone className="w-3 h-3" />{prop.tenant_phone}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-muted-foreground">Vacant</span>
                                )}
                                <Edit className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-medium text-emerald-600">
                                £{(prop.rent_amount || 0).toLocaleString()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-1">
                                <Button size="sm" variant="ghost" onClick={(e) => handleEditProperty(prop, e)} title="Edit Landlord">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDeletePropertyModal(prop);
                                  }} 
                                  title="Delete Property"
                                  className="text-slate-500 hover:text-red-600"
                                  data-testid={`delete-property-${prop.property_id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination Controls */}
                  {filteredProperties.length > propertiesPerPage && (
                    <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                      <p className="text-sm text-slate-600">
                        Showing {((propertiesPage - 1) * propertiesPerPage) + 1} - {Math.min(propertiesPage * propertiesPerPage, filteredProperties.length)} of {filteredProperties.length}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPropertiesPage(1)}
                          disabled={propertiesPage === 1}
                          data-testid="properties-first-page"
                        >
                          First
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPropertiesPage(p => Math.max(1, p - 1))}
                          disabled={propertiesPage === 1}
                          data-testid="properties-prev-page"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm px-3 py-1 bg-white border rounded">
                          Page {propertiesPage} of {Math.ceil(filteredProperties.length / propertiesPerPage)}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPropertiesPage(p => Math.min(Math.ceil(filteredProperties.length / propertiesPerPage), p + 1))}
                          disabled={propertiesPage >= Math.ceil(filteredProperties.length / propertiesPerPage)}
                          data-testid="properties-next-page"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPropertiesPage(Math.ceil(filteredProperties.length / propertiesPerPage))}
                          disabled={propertiesPage >= Math.ceil(filteredProperties.length / propertiesPerPage)}
                          data-testid="properties-last-page"
                        >
                          Last
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* MAINTENANCE SECTION */}
          {activeSection === 'maintenance' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className="bg-amber-100 text-amber-700">{activeJobs.length} Active</Badge>
                  <Badge variant="secondary">{maintenanceJobs.filter(j => j.status === 'completed').length} Completed</Badge>
                </div>
              </div>

              <div className="grid gap-4">
                {activeJobs.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500 opacity-50" />
                      <p className="text-muted-foreground">No active maintenance jobs</p>
                    </CardContent>
                  </Card>
                ) : (
                  activeJobs.map((job) => (
                    <Card key={job.job_id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={
                                job.status === 'completed' ? 'bg-green-100 text-green-700' :
                                job.status === 'quote_required' || job.status === 'quote_pending' ? 'bg-purple-100 text-purple-700' :
                                job.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {job.status?.replace(/_/g, ' ')}
                              </Badge>
                              {job.is_emergency && (
                                <Badge className="bg-red-100 text-red-700">Emergency</Badge>
                              )}
                            </div>
                            <p className="font-medium">{job.issue_type || 'Maintenance Issue'}</p>
                            {job.property_address && (
                              <p className="text-sm text-blue-600">{job.property_address}</p>
                            )}
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {job.description}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              {job.tenant_name && (
                                <span className="text-slate-600">
                                  <User className="w-3 h-3 inline mr-1" />
                                  {job.tenant_name}
                                </span>
                              )}
                              {job.contractor_name && (
                                <span className="text-purple-600">
                                  <Wrench className="w-3 h-3 inline mr-1" />
                                  {job.contractor_name}
                                </span>
                              )}
                            </div>
                            {job.quote_amount && (
                              <p className="text-sm font-medium text-purple-600 mt-2">
                                Quote: £{job.quote_amount.toFixed(2)}
                                {job.quote_approved && <span className="text-green-600 ml-2">✓ Approved</span>}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {job.scheduled_time && (
                              <>
                                <p className="text-sm font-medium text-blue-600">
                                  {new Date(job.scheduled_time).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(job.scheduled_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">#{job.job_id?.slice(-6)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* LANDLORDS SECTION */}
          {activeSection === 'landlords' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{landlords.length} landlords</p>
                <Button onClick={() => setShowAddLandlordModal(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Landlord
                </Button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {landlords.slice(0, 30).map((ll) => (
                  <Card 
                    key={ll.landlord_id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/admin/lettings/landlord/${ll.landlord_id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-blue-600">{ll.name}</p>
                          {ll.email && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <Mail className="w-3 h-3" /> {ll.email}
                            </p>
                          )}
                          {ll.phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {ll.phone}
                            </p>
                          )}
                        </div>
                        <Badge className={ll.portal_opened ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                          {ll.portal_opened ? 'Active' : ll.portal_invited ? 'Invited' : 'No Portal'}
                        </Badge>
                      </div>
                      <div className="mt-3 pt-3 border-t text-sm text-muted-foreground">
                        {ll.properties_count || 0} properties
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* INSTRUCTIONS SECTION */}
          {activeSection === 'instructions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">New property instructions</p>
                <Button onClick={() => navigate('/lettings/instruct')}>
                  <Plus className="w-4 h-4 mr-1" /> New Instruction
                </Button>
              </div>

              <Card>
                <CardContent className="py-12 text-center">
                  <FileInput className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p className="font-medium">Property Instructions</p>
                  <p className="text-sm text-muted-foreground mt-1">Start a new letting instruction or view existing ones</p>
                  <Button className="mt-4" onClick={() => navigate('/lettings/instruct')}>
                    <Plus className="w-4 h-4 mr-1" /> New Instruction
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* DIARY SECTION */}
          {activeSection === 'diary' && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Diary</CardTitle>
                  <CardDescription>View scheduled viewings, inspections, and appointments</CardDescription>
                </CardHeader>
                <CardContent className="py-8 text-center">
                  <CalendarDays className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p className="text-muted-foreground">Diary coming soon</p>
                  <p className="text-sm text-muted-foreground mt-1">This will show your scheduled tasks and appointments</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* APPLICANTS SECTION */}
          {activeSection === 'applicants' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">{applicants.length} applicants</p>
                  <Input
                    placeholder="Search applicants..."
                    value={applicantSearch}
                    onChange={(e) => setApplicantSearch(e.target.value)}
                    className="w-64"
                  />
                </div>
                <Button onClick={() => setShowAddApplicantModal(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Applicant
                </Button>
              </div>

              {applicantsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4 font-medium text-sm">Name</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Contact</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Requirements</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Move In</th>
                            <th className="text-center py-3 px-4 font-medium text-sm">Type</th>
                            <th className="text-center py-3 px-4 font-medium text-sm">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {applicants
                            .filter(app => {
                              if (!applicantSearch) return true;
                              const search = applicantSearch.toLowerCase();
                              return (
                                app.display_name?.toLowerCase().includes(search) ||
                                app.email?.toLowerCase().includes(search) ||
                                app.phone?.includes(search) ||
                                app.mobile?.includes(search) ||
                                app.preferred_areas?.toLowerCase().includes(search)
                              );
                            })
                            .slice(0, 100)
                            .map((applicant) => (
                              <tr 
                                key={applicant.applicant_id} 
                                className="border-b hover:bg-slate-50 cursor-pointer"
                              >
                                <td className="py-3 px-4">
                                  <p className="font-medium text-blue-600">{applicant.display_name || `${applicant.first_name} ${applicant.last_name}`}</p>
                                  {applicant.current_postcode && (
                                    <p className="text-xs text-muted-foreground">{applicant.current_postcode}</p>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-sm">
                                  {applicant.email && <p className="text-muted-foreground">{applicant.email}</p>}
                                  {applicant.mobile && <p className="text-muted-foreground">{applicant.mobile}</p>}
                                  {applicant.phone && !applicant.mobile && <p className="text-muted-foreground">{applicant.phone}</p>}
                                </td>
                                <td className="py-3 px-4 text-sm">
                                  <div className="space-y-0.5">
                                    {applicant.max_rent && <p>Max £{applicant.max_rent} pcm</p>}
                                    {applicant.min_bedrooms && <p>{applicant.min_bedrooms}+ beds</p>}
                                    {applicant.preferred_areas && <p className="text-muted-foreground text-xs">{applicant.preferred_areas}</p>}
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-sm">
                                  {applicant.move_in_date || '-'}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <Badge variant="outline" className="capitalize">
                                    {applicant.applicant_type || 'tenant'}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <Badge className={
                                    applicant.status === 'active' ? 'bg-green-100 text-green-700' :
                                    applicant.status === 'matched' ? 'bg-blue-100 text-blue-700' :
                                    applicant.status === 'archived' ? 'bg-slate-100 text-slate-600' :
                                    'bg-amber-100 text-amber-700'
                                  }>
                                    {applicant.status || 'active'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {applicants.length === 0 && !applicantsLoading && (
                      <div className="py-12 text-center">
                        <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-muted-foreground">No applicants found</p>
                        <Button className="mt-4" onClick={() => setShowAddApplicantModal(true)}>
                          <Plus className="w-4 h-4 mr-1" /> Add First Applicant
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* TENANTS SECTION (Secondary) */}
          {activeSection === 'tenants' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{tenants.length} tenants</p>
                <Button onClick={() => setShowAddTenantModal(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Tenant
                </Button>
              </div>

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-3 px-4 font-medium text-sm">Tenant</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Contact</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Property</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Tenancy</th>
                          <th className="text-left py-3 px-4 font-medium text-sm">Landlord</th>
                          <th className="text-center py-3 px-4 font-medium text-sm">Status</th>
                          <th className="text-center py-3 px-4 font-medium text-sm">Portal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenants.slice(0, 50).map((tenant) => (
                          <tr 
                            key={tenant.tenant_id} 
                            className="border-b hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`/admin/lettings/tenant/${tenant.tenant_id}`)}
                          >
                            <td className="py-3 px-4">
                              <p className="font-medium text-blue-600">{tenant.name}</p>
                              {tenant.tenancy_reference && (
                                <p className="text-xs text-muted-foreground font-mono">{tenant.tenancy_reference}</p>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm">{tenant.email || '-'}</p>
                              {tenant.phone && (
                                <p className="text-xs text-muted-foreground">{tenant.phone}</p>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm">{tenant.property_address || tenant.property?.address || '-'}</p>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-xs">
                                <p className="text-muted-foreground">
                                  {tenant.start_date || 'N/A'} - {tenant.end_date || 'Ongoing'}
                                </p>
                                {tenant.rent_amount && (
                                  <p className="font-medium text-emerald-600">
                                    £{tenant.rent_amount?.toLocaleString()} {tenant.rent_frequency || 'pcm'}
                                  </p>
                                )}
                                {tenant.is_periodic && (
                                  <Badge variant="outline" className="text-xs mt-1">Periodic</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {tenant.landlord?.name ? (
                                <p className="text-sm">{tenant.landlord.name}</p>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge className={
                                tenant.status === 'active' ? 'bg-green-100 text-green-700' : 
                                tenant.status === 'notice' ? 'bg-amber-100 text-amber-700' :
                                tenant.status === 'vacated' ? 'bg-red-100 text-red-700' :
                                tenant.status === 'archived' ? 'bg-slate-200 text-slate-600' :
                                'bg-slate-100'
                              }>
                                {tenant.status || 'unknown'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge className={
                                tenant.portal_opened ? 'bg-green-100 text-green-700' :
                                tenant.portal_invited ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-600'
                              }>
                                {tenant.portal_opened ? 'Active' : tenant.portal_invited ? 'Invited' : 'None'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CONTRACTORS SECTION (Secondary) */}
          {activeSection === 'contractors' && (
            <div className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Contractors</CardTitle>
                    <CardDescription>Manage your approved contractors</CardDescription>
                  </div>
                  <Button onClick={() => setShowAddContractorModal(true)} data-testid="add-contractor-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Contractor
                  </Button>
                </CardHeader>
                <CardContent>
                  {contractors.length === 0 ? (
                    <div className="py-8 text-center">
                      <Wrench className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p className="text-muted-foreground mb-4">No contractors added yet</p>
                      <Button variant="outline" onClick={() => setShowAddContractorModal(true)}>
                        <Plus className="w-4 h-4 mr-1" /> Add Your First Contractor
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contractors.map((contractor) => {
                        const statusInfo = getContractorStatus(contractor);
                        const StatusIcon = statusInfo.icon;
                        const contractorId = contractor.contractor_id || contractor.id;
                        
                        return (
                          <div 
                            key={contractorId} 
                            className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                              statusInfo.isActive 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                statusInfo.isActive ? 'bg-green-100' : 'bg-blue-100'
                              }`}>
                                <Wrench className={`w-5 h-5 ${statusInfo.isActive ? 'text-green-600' : 'text-blue-600'}`} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{contractor.name}</p>
                                <div className="flex items-center gap-3 text-sm text-slate-500">
                                  {/* Status Badge */}
                                  <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                                    {StatusIcon && <StatusIcon className="w-3 h-3" />}
                                    {statusInfo.label}
                                  </span>
                                  {contractor.specialty && (
                                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">{contractor.specialty}</span>
                                  )}
                                  {contractor.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" /> {contractor.phone}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Show Send Invite button only if not invited/active */}
                              {!statusInfo.isActive && !['invited', 'onboarding', 'pending_approval'].includes(contractor.status) && contractor.email && (
                                <Button 
                                  variant="default"
                                  size="sm"
                                  onClick={() => handleSendInvite(contractor)}
                                  disabled={invitingContractor === contractorId}
                                  className="bg-blue-600 hover:bg-blue-700"
                                  data-testid={`invite-contractor-${contractorId}`}
                                >
                                  {invitingContractor === contractorId ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4 mr-1" />
                                  )}
                                  Send Invite
                                </Button>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate(`/admin/contractors/${contractorId}`)}
                                data-testid={`contractor-details-${contractorId}`}
                              >
                                Details
                              </Button>
                              {contractor.email && (
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={`mailto:${contractor.email}`}>
                                    <Mail className="w-4 h-4" />
                                  </a>
                                </Button>
                              )}
                              {contractor.phone && (
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={`tel:${contractor.phone}`}>
                                    <Phone className="w-4 h-4" />
                                  </a>
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteContractor(contractorId)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-contractor-${contractorId}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* SETTINGS SECTION (Secondary) */}
          {activeSection === 'settings' && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Lettings Settings</CardTitle>
                  <CardDescription>Configure default settings for lettings</CardDescription>
                </CardHeader>
                <CardContent className="py-8 text-center">
                  <Settings className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p className="text-muted-foreground">Settings panel coming soon</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Add Property Modal */}
      <Dialog open={showAddPropertyModal} onOpenChange={setShowAddPropertyModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Property</DialogTitle>
            <DialogDescription>Add a new property to your lettings portfolio</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Postcode Lookup */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <PostcodeLookup 
                onAddressSelect={(addressData) => {
                  setPropertyForm(prev => ({
                    ...prev,
                    address_line1: addressData.line_1 || "",
                    address_line2: [addressData.line_2, addressData.line_3].filter(Boolean).join(", ") || "",
                    town: addressData.town || "",
                    postcode: addressData.postcode || ""
                  }));
                  toast.success("Address selected");
                }}
              />
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-800">Property Address</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Address Line 1 *</Label>
                  <Input 
                    value={propertyForm.address_line1}
                    onChange={(e) => setPropertyForm({...propertyForm, address_line1: e.target.value})}
                    placeholder="e.g. 123 High Street"
                    data-testid="property-address1"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address Line 2</Label>
                  <Input 
                    value={propertyForm.address_line2}
                    onChange={(e) => setPropertyForm({...propertyForm, address_line2: e.target.value})}
                    placeholder="Flat 2, Building Name, etc."
                    data-testid="property-address2"
                  />
                </div>
                <div>
                  <Label>Town/City</Label>
                  <Input 
                    value={propertyForm.town}
                    onChange={(e) => setPropertyForm({...propertyForm, town: e.target.value})}
                    placeholder="e.g. London"
                    data-testid="property-town"
                  />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <Input 
                    value={propertyForm.postcode}
                    onChange={(e) => setPropertyForm({...propertyForm, postcode: e.target.value})}
                    placeholder="e.g. SW1A 1AA"
                    data-testid="property-postcode"
                  />
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-slate-800">Property Details</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Property Type</Label>
                  <Select 
                    value={propertyForm.property_type} 
                    onValueChange={(value) => setPropertyForm({...propertyForm, property_type: value})}
                  >
                    <SelectTrigger data-testid="property-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="flat">Flat</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="bungalow">Bungalow</SelectItem>
                      <SelectItem value="maisonette">Maisonette</SelectItem>
                      <SelectItem value="studio">Studio</SelectItem>
                      <SelectItem value="room">Room</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bedrooms</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={propertyForm.bedrooms}
                    onChange={(e) => setPropertyForm({...propertyForm, bedrooms: e.target.value})}
                    placeholder="0"
                    data-testid="property-bedrooms"
                  />
                </div>
                <div>
                  <Label>Bathrooms</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={propertyForm.bathrooms}
                    onChange={(e) => setPropertyForm({...propertyForm, bathrooms: e.target.value})}
                    placeholder="0"
                    data-testid="property-bathrooms"
                  />
                </div>
              </div>
            </div>

            {/* Landlord & Rent */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-slate-800">Landlord & Rent</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Landlord</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={propertyForm.landlord_id} 
                      onValueChange={(value) => {
                        if (value === "new") {
                          setPropertyForm({...propertyForm, landlord_id: "", createNewLandlord: true});
                        } else {
                          setPropertyForm({...propertyForm, landlord_id: value === "none" ? "" : value, createNewLandlord: false});
                        }
                      }}
                    >
                      <SelectTrigger data-testid="property-landlord" className="flex-1">
                        <SelectValue placeholder="Select landlord" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No landlord assigned</SelectItem>
                        <SelectItem value="new" className="text-blue-600 font-medium">+ Add New Landlord</SelectItem>
                        {landlords.map(landlord => (
                          <SelectItem key={landlord.landlord_id || landlord.id} value={landlord.landlord_id || landlord.id}>
                            {landlord.name || `${landlord.first_name || ''} ${landlord.last_name || ''}`.trim()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Inline New Landlord Form */}
                {propertyForm.createNewLandlord && (
                  <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-blue-800 flex items-center gap-2">
                      <Plus className="w-4 h-4" /> New Landlord Details
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Full Name *</Label>
                        <Input 
                          value={propertyForm.newLandlord?.name || ""}
                          onChange={(e) => setPropertyForm({
                            ...propertyForm, 
                            newLandlord: {...(propertyForm.newLandlord || {}), name: e.target.value}
                          })}
                          placeholder="e.g. John Smith"
                          data-testid="new-landlord-name"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Email *</Label>
                        <Input 
                          type="email"
                          value={propertyForm.newLandlord?.email || ""}
                          onChange={(e) => setPropertyForm({
                            ...propertyForm, 
                            newLandlord: {...(propertyForm.newLandlord || {}), email: e.target.value}
                          })}
                          placeholder="e.g. john@email.com"
                          data-testid="new-landlord-email"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Phone</Label>
                        <Input 
                          value={propertyForm.newLandlord?.phone || ""}
                          onChange={(e) => setPropertyForm({
                            ...propertyForm, 
                            newLandlord: {...(propertyForm.newLandlord || {}), phone: e.target.value}
                          })}
                          placeholder="e.g. 07xxx xxxxxx"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Address</Label>
                        <Input 
                          value={propertyForm.newLandlord?.address_line1 || ""}
                          onChange={(e) => setPropertyForm({
                            ...propertyForm, 
                            newLandlord: {...(propertyForm.newLandlord || {}), address_line1: e.target.value}
                          })}
                          placeholder="Landlord's address"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-blue-600">Bank details can be added later when sending the LMA</p>
                  </div>
                )}
                
                <div>
                  <Label>Monthly Rent (£)</Label>
                  <Input 
                    type="number"
                    min="0"
                    step="0.01"
                    value={propertyForm.rent_amount}
                    onChange={(e) => setPropertyForm({...propertyForm, rent_amount: e.target.value})}
                    placeholder="e.g. 1500"
                    data-testid="property-rent"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPropertyModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAddProperty} 
              disabled={savingProperty || !propertyForm.address_line1}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-property-submit"
            >
              {savingProperty ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tenant Modal */}
      <Dialog open={showAddTenantModal} onOpenChange={setShowAddTenantModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Tenant</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-muted-foreground">
            <p>Tenant form coming soon</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTenantModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Landlord Modal */}
      <Dialog open={showAddLandlordModal} onOpenChange={setShowAddLandlordModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Landlord</DialogTitle>
            <DialogDescription>Register a new landlord to your lettings portfolio</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-800">Landlord Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name *</Label>
                  <Input 
                    value={landlordForm.name}
                    onChange={(e) => setLandlordForm({...landlordForm, name: e.target.value})}
                    placeholder="e.g. John Smith"
                    data-testid="landlord-name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={landlordForm.email}
                    onChange={(e) => setLandlordForm({...landlordForm, email: e.target.value})}
                    placeholder="e.g. john@example.com"
                    data-testid="landlord-email"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input 
                    value={landlordForm.phone}
                    onChange={(e) => setLandlordForm({...landlordForm, phone: e.target.value})}
                    placeholder="e.g. 07xxx xxxxxx"
                    data-testid="landlord-phone"
                  />
                </div>
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-slate-800">Correspondence Address</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Address Line 1</Label>
                  <Input 
                    value={landlordForm.address_line1}
                    onChange={(e) => setLandlordForm({...landlordForm, address_line1: e.target.value})}
                    placeholder="e.g. 123 High Street"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Address Line 2</Label>
                  <Input 
                    value={landlordForm.address_line2}
                    onChange={(e) => setLandlordForm({...landlordForm, address_line2: e.target.value})}
                    placeholder="Flat 2, Building Name, etc."
                  />
                </div>
                <div>
                  <Label>Town/City</Label>
                  <Input 
                    value={landlordForm.town}
                    onChange={(e) => setLandlordForm({...landlordForm, town: e.target.value})}
                    placeholder="e.g. London"
                  />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <Input 
                    value={landlordForm.postcode}
                    onChange={(e) => setLandlordForm({...landlordForm, postcode: e.target.value})}
                    placeholder="e.g. SW1A 1AA"
                  />
                </div>
              </div>
            </div>
            
            {/* Bank Details */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-slate-800">Bank Details (for rent payments)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Bank Name</Label>
                  <Input 
                    value={landlordForm.bank_name}
                    onChange={(e) => setLandlordForm({...landlordForm, bank_name: e.target.value})}
                    placeholder="e.g. Barclays"
                  />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input 
                    value={landlordForm.account_number}
                    onChange={(e) => setLandlordForm({...landlordForm, account_number: e.target.value})}
                    placeholder="e.g. 12345678"
                  />
                </div>
                <div>
                  <Label>Sort Code</Label>
                  <Input 
                    value={landlordForm.sort_code}
                    onChange={(e) => setLandlordForm({...landlordForm, sort_code: e.target.value})}
                    placeholder="e.g. 20-00-00"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLandlordModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAddLandlord} 
              disabled={savingLandlord || !landlordForm.name}
              data-testid="add-landlord-submit"
            >
              {savingLandlord ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <>Add Landlord</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contractor Modal */}
      <Dialog open={showAddContractorModal} onOpenChange={setShowAddContractorModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Contractor</DialogTitle>
            <DialogDescription>Add a contractor to your approved list</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="contractor-name">Name *</Label>
              <Input
                id="contractor-name"
                value={contractorForm.name}
                onChange={(e) => setContractorForm({...contractorForm, name: e.target.value})}
                placeholder="John Smith"
                data-testid="contractor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contractor-company">Company Name</Label>
              <Input
                id="contractor-company"
                value={contractorForm.company_name}
                onChange={(e) => setContractorForm({...contractorForm, company_name: e.target.value})}
                placeholder="Smith's Plumbing Ltd"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contractor-phone">Phone *</Label>
                <Input
                  id="contractor-phone"
                  value={contractorForm.phone}
                  onChange={(e) => setContractorForm({...contractorForm, phone: e.target.value})}
                  placeholder="07xxx xxxxxx"
                  data-testid="contractor-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contractor-email">Email</Label>
                <Input
                  id="contractor-email"
                  type="email"
                  value={contractorForm.email}
                  onChange={(e) => setContractorForm({...contractorForm, email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contractor-specialty">Specialty</Label>
              <Select 
                value={contractorForm.specialty} 
                onValueChange={(value) => setContractorForm({...contractorForm, specialty: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select specialty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plumber">Plumber</SelectItem>
                  <SelectItem value="electrician">Electrician</SelectItem>
                  <SelectItem value="gas_engineer">Gas Engineer</SelectItem>
                  <SelectItem value="handyman">Handyman</SelectItem>
                  <SelectItem value="locksmith">Locksmith</SelectItem>
                  <SelectItem value="roofer">Roofer</SelectItem>
                  <SelectItem value="builder">Builder</SelectItem>
                  <SelectItem value="decorator">Decorator</SelectItem>
                  <SelectItem value="cleaner">Cleaner</SelectItem>
                  <SelectItem value="gardener">Gardener</SelectItem>
                  <SelectItem value="pest_control">Pest Control</SelectItem>
                  <SelectItem value="appliance_repair">Appliance Repair</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contractor-notes">Notes</Label>
              <Input
                id="contractor-notes"
                value={contractorForm.notes}
                onChange={(e) => setContractorForm({...contractorForm, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContractorModal(false)}>Cancel</Button>
            <Button onClick={handleAddContractor} disabled={savingContractor} data-testid="save-contractor-btn">
              {savingContractor ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Contractor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Applicant Modal */}
      <Dialog open={showAddApplicantModal} onOpenChange={setShowAddApplicantModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lettings Applicant</DialogTitle>
            <DialogDescription>Register a new tenant looking for a property</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Personal Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  value={newApplicant.first_name}
                  onChange={(e) => setNewApplicant({...newApplicant, first_name: e.target.value})}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Last Name *</label>
                <Input
                  value={newApplicant.last_name}
                  onChange={(e) => setNewApplicant({...newApplicant, last_name: e.target.value})}
                  placeholder="Last name"
                />
              </div>
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={newApplicant.email}
                  onChange={(e) => setNewApplicant({...newApplicant, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Mobile</label>
                <Input
                  value={newApplicant.mobile}
                  onChange={(e) => setNewApplicant({...newApplicant, mobile: e.target.value})}
                  placeholder="07xxx xxxxxx"
                />
              </div>
            </div>

            {/* Current Address */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium">Current Address</label>
                <Input
                  value={newApplicant.current_address}
                  onChange={(e) => setNewApplicant({...newApplicant, current_address: e.target.value})}
                  placeholder="Current address"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Postcode</label>
                <Input
                  value={newApplicant.current_postcode}
                  onChange={(e) => setNewApplicant({...newApplicant, current_postcode: e.target.value})}
                  placeholder="MK42 xxx"
                />
              </div>
            </div>

            {/* Requirements */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Property Requirements</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Max Rent (£ pcm)</label>
                  <Input
                    type="number"
                    value={newApplicant.max_rent}
                    onChange={(e) => setNewApplicant({...newApplicant, max_rent: e.target.value})}
                    placeholder="1200"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Min Bedrooms</label>
                  <Select
                    value={newApplicant.min_bedrooms}
                    onValueChange={(value) => setNewApplicant({...newApplicant, min_bedrooms: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1+</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="3">3+</SelectItem>
                      <SelectItem value="4">4+</SelectItem>
                      <SelectItem value="5">5+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Move In Date</label>
                  <Input
                    type="date"
                    value={newApplicant.move_in_date}
                    onChange={(e) => setNewApplicant({...newApplicant, move_in_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2 mt-3">
                <label className="text-sm text-muted-foreground">Preferred Areas</label>
                <Input
                  value={newApplicant.preferred_areas}
                  onChange={(e) => setNewApplicant({...newApplicant, preferred_areas: e.target.value})}
                  placeholder="e.g. Kempston, Bedford Town Centre, Queens Park"
                />
              </div>
            </div>

            {/* Letting Classification */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Letting Classification</p>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.letting_long_let}
                    onChange={(e) => setNewApplicant({...newApplicant, letting_long_let: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Long-Let
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.letting_short_let}
                    onChange={(e) => setNewApplicant({...newApplicant, letting_short_let: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Short-Let
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.letting_student}
                    onChange={(e) => setNewApplicant({...newApplicant, letting_student: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Student
                </label>
              </div>
            </div>

            {/* Furnishing */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Furnishing</p>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.furnishing_fully}
                    onChange={(e) => setNewApplicant({...newApplicant, furnishing_fully: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Fully
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.furnishing_either}
                    onChange={(e) => setNewApplicant({...newApplicant, furnishing_either: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Furnished/unfurnished
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.furnishing_part}
                    onChange={(e) => setNewApplicant({...newApplicant, furnishing_part: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Part
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.furnishing_unfurnished}
                    onChange={(e) => setNewApplicant({...newApplicant, furnishing_unfurnished: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Unfurnished
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.furnishing_unfurnished_white}
                    onChange={(e) => setNewApplicant({...newApplicant, furnishing_unfurnished_white: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Unfurnished + white appliances
                </label>
              </div>
            </div>

            {/* Employment */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Employment Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Employment Status</label>
                  <Select
                    value={newApplicant.employment_status}
                    onValueChange={(value) => setNewApplicant({...newApplicant, employment_status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employed">Employed</SelectItem>
                      <SelectItem value="self_employed">Self Employed</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                      <SelectItem value="unemployed">Unemployed</SelectItem>
                      <SelectItem value="benefits">On Benefits</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Applicant Type</label>
                  <Select
                    value={newApplicant.applicant_type}
                    onValueChange={(value) => setNewApplicant({...newApplicant, applicant_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tenant">Tenant</SelectItem>
                      <SelectItem value="guarantor">Guarantor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="border-t pt-4">
              <div className="flex gap-6 mb-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.has_pets}
                    onChange={(e) => setNewApplicant({...newApplicant, has_pets: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Has Pets
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.is_smoker}
                    onChange={(e) => setNewApplicant({...newApplicant, is_smoker: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Smoker
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={newApplicant.use_dear_sir_madam}
                    onChange={(e) => setNewApplicant({...newApplicant, use_dear_sir_madam: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  Use Dear Sir/Madam
                </label>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Enables Dear Sir / Madam to be set appropriately in correspondence</p>
              
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground">Notes</label>
                <textarea
                  value={newApplicant.notes}
                  onChange={(e) => setNewApplicant({...newApplicant, notes: e.target.value})}
                  placeholder="Any additional notes about the applicant..."
                  className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm"
                />
              </div>
            </div>

            {/* Additional Contact */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">Additional Contact</p>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAdditionalContact(!showAdditionalContact)}
                >
                  {showAdditionalContact ? 'Hide' : 'Add additional contact details'}
                </Button>
              </div>
              {showAdditionalContact && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Name</label>
                    <Input
                      value={newApplicant.additional_contact_name}
                      onChange={(e) => setNewApplicant({...newApplicant, additional_contact_name: e.target.value})}
                      placeholder="Contact name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Phone</label>
                    <Input
                      value={newApplicant.additional_contact_phone}
                      onChange={(e) => setNewApplicant({...newApplicant, additional_contact_phone: e.target.value})}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Email</label>
                    <Input
                      value={newApplicant.additional_contact_email}
                      onChange={(e) => setNewApplicant({...newApplicant, additional_contact_email: e.target.value})}
                      placeholder="Email address"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddApplicantModal(false)}>Cancel</Button>
            <Button onClick={handleAddApplicant} disabled={savingApplicant}>
              {savingApplicant ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add Applicant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Property Modal (Change Landlord) */}
      <Dialog open={showEditPropertyModal} onOpenChange={setShowEditPropertyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Property</DialogTitle>
            <DialogDescription>
              {editingProperty?.address}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Assign Landlord</Label>
              <Select 
                value={editingProperty?.landlord_id || "none"} 
                onValueChange={(value) => setEditingProperty({
                  ...editingProperty, 
                  landlord_id: value === "none" ? "" : value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select landlord" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No landlord assigned</SelectItem>
                  {landlords.map(landlord => (
                    <SelectItem key={landlord.landlord_id || landlord.id} value={landlord.landlord_id || landlord.id}>
                      {landlord.name || `${landlord.first_name || ''} ${landlord.last_name || ''}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPropertyModal(false)}>Cancel</Button>
            <Button onClick={handleSavePropertyEdit} disabled={savingPropertyEdit}>
              {savingPropertyEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Property Confirmation Modal */}
      <Dialog open={!!showDeletePropertyModal} onOpenChange={() => setShowDeletePropertyModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Property</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this property? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {showDeletePropertyModal && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-slate-800">{showDeletePropertyModal.address}</p>
                <p className="text-sm text-slate-500 mt-1">{showDeletePropertyModal.postcode}</p>
                {showDeletePropertyModal.landlord_name && (
                  <p className="text-sm text-slate-500">Landlord: {showDeletePropertyModal.landlord_name}</p>
                )}
                {showDeletePropertyModal.tenant_name && (
                  <p className="text-sm text-slate-500">Tenant: {showDeletePropertyModal.tenant_name}</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDeletePropertyModal(null)}
              disabled={deletingProperty}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteProperty} 
              disabled={deletingProperty}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-property"
            >
              {deletingProperty ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={showBulkDeleteModal} onOpenChange={setShowBulkDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete {selectedProperties.size} Properties</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedProperties.size} {selectedProperties.size === 1 ? 'property' : 'properties'}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
              {filteredProperties
                .filter(p => selectedProperties.has(p.property_id))
                .map(prop => (
                  <div key={prop.property_id} className="py-1 border-b last:border-0">
                    <p className="font-medium text-sm text-slate-800">{prop.address}</p>
                    <p className="text-xs text-slate-500">{prop.postcode}</p>
                  </div>
                ))
              }
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowBulkDeleteModal(false)}
              disabled={bulkDeleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDeleteProperties} 
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete-properties"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete {selectedProperties.size} Properties
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Name Modal */}
      <Dialog open={!!editingTenantProp} onOpenChange={() => setEditingTenantProp(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogDescription>
              Update or clear the tenant name for {editingTenantProp?.address || editingTenantProp?.property_address}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Tenant Name</Label>
              <Input
                value={editTenantName}
                onChange={(e) => setEditTenantName(e.target.value)}
                placeholder="Enter tenant name or leave blank to clear"
                data-testid="edit-tenant-name-input"
              />
            </div>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="destructive"
              onClick={() => handleUpdateTenantName(true)}
              disabled={savingTenantEdit}
              data-testid="clear-tenant-btn"
            >
              {savingTenantEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear Tenant
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setEditingTenantProp(null)}
                disabled={savingTenantEdit}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleUpdateTenantName(false)}
                disabled={savingTenantEdit}
                data-testid="save-tenant-btn"
              >
                {savingTenantEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LettingsPage;
